import React, { useState, useEffect, useCallback } from 'react';
import { Loader2, AlertCircle, Trash2, RotateCcw } from 'lucide-react';
import { api } from './adminHelpers';

const STAGE_LABELS = {
  lead: 'Lead',
  prospect: 'Prospect',
  onboarding: 'Onboarding',
  onboarding_sales: 'Onboarding (Sales)',
  onboarding_backoffice: 'Onboarding (Backoffice)',
  client: 'Klant',
};

function formatDateTime(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('nl-NL', {
    day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
}

function TrashTab() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [busyId, setBusyId] = useState(null);

  const fetchTrash = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api('/api/v1/admin/trash');
      setItems(res.items || []);
    } catch (err) { setError(err.message); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchTrash(); }, [fetchTrash]);

  const handleRestore = async (item) => {
    setBusyId(item.id);
    try {
      await api(`/api/v1/admin/trash/${item.id}/restore`, { method: 'POST' });
      await fetchTrash();
    } catch (err) { alert('Herstellen mislukt: ' + err.message); }
    finally { setBusyId(null); }
  };

  const handlePurge = async (item) => {
    if (!window.confirm(
      `"${item.company_name}" DEFINITIEF verwijderen?\n\n` +
      'Alle gekoppelde data (notities, documenten, gesprekken, contacten, ' +
      'forecasting, compliance) verdwijnt onherroepelijk.\n\n' +
      'Dit kan NIET ongedaan worden gemaakt.'
    )) return;
    setBusyId(item.id);
    try {
      await api(`/api/v1/admin/trash/${item.id}/purge`, { method: 'DELETE' });
      await fetchTrash();
    } catch (err) { alert('Definitief verwijderen mislukt: ' + err.message); }
    finally { setBusyId(null); }
  };

  if (loading) return (
    <div className="flex items-center justify-center py-20">
      <Loader2 size={32} className="animate-spin" style={{ color: '#3d61a4' }} />
    </div>
  );

  if (error) return (
    <div className="bg-red-50 text-red-600 rounded-xl p-4 flex items-center gap-3">
      <AlertCircle size={20} /><span className="text-sm">{error}</span>
    </div>
  );

  if (!items.length) return (
    <div className="text-center py-16 bg-white rounded-xl border border-[#e8eaf2]">
      <Trash2 size={40} className="mx-auto mb-3" style={{ color: '#cdd1e0' }} />
      <p className="font-semibold" style={{ color: '#011745' }}>Prullenbak is leeg</p>
      <p className="text-sm mt-1" style={{ color: '#7b859e' }}>
        Verwijderde leads, prospects en klanten verschijnen hier
      </p>
    </div>
  );

  return (
    <div className="bg-white rounded-xl border border-[#e8eaf2] shadow-sm">
      <div className="px-6 py-4 border-b border-[#e8eaf2] flex items-center justify-between">
        <h3 className="text-sm font-bold" style={{ color: '#011745' }}>
          Prullenbak ({items.length})
        </h3>
        <p className="text-xs" style={{ color: '#7b859e' }}>
          Herstel of verwijder definitief — definitief verwijderen wist alle gekoppelde data
        </p>
      </div>
      <table className="w-full">
        <thead>
          <tr className="border-b border-[#e8eaf2]">
            <th className="text-left text-xs font-semibold uppercase px-6 py-3" style={{ color: '#7b859e' }}>Bedrijfsnaam</th>
            <th className="text-left text-xs font-semibold uppercase px-6 py-3" style={{ color: '#7b859e' }}>Fase</th>
            <th className="text-left text-xs font-semibold uppercase px-6 py-3" style={{ color: '#7b859e' }}>Verwijderd op</th>
            <th className="text-left text-xs font-semibold uppercase px-6 py-3" style={{ color: '#7b859e' }}>Verwijderd door</th>
            <th className="text-right text-xs font-semibold uppercase px-6 py-3" style={{ color: '#7b859e' }}>Acties</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-[#f3f4f8]">
          {items.map(item => (
            <tr key={item.id} className="hover:bg-[#f7f8fc] transition-colors">
              <td className="px-6 py-3">
                <p className="text-sm font-semibold" style={{ color: '#011745' }}>{item.company_name || '—'}</p>
                {item.contact_name && (
                  <p className="text-xs" style={{ color: '#7b859e' }}>{item.contact_name}</p>
                )}
              </td>
              <td className="px-6 py-3">
                <span className="text-xs font-semibold px-2.5 py-1 rounded-lg"
                  style={{ backgroundColor: '#eef2fa', color: '#3d61a4' }}>
                  {STAGE_LABELS[item.pipeline_stage] || item.pipeline_stage || '—'}
                </span>
              </td>
              <td className="px-6 py-3 text-sm" style={{ color: '#566079' }}>{formatDateTime(item.deleted_at)}</td>
              <td className="px-6 py-3 text-sm" style={{ color: '#566079' }}>{item.deleted_by_name || '—'}</td>
              <td className="px-6 py-3">
                <div className="flex items-center justify-end gap-2">
                  <button
                    onClick={() => handleRestore(item)}
                    disabled={busyId === item.id}
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all disabled:opacity-50"
                    style={{ backgroundColor: '#eef2fa', color: '#3d61a4' }}>
                    {busyId === item.id
                      ? <Loader2 size={13} className="animate-spin" />
                      : <RotateCcw size={13} />}
                    Herstellen
                  </button>
                  <button
                    onClick={() => handlePurge(item)}
                    disabled={busyId === item.id}
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold bg-red-50 text-red-600 hover:bg-red-100 transition-all disabled:opacity-50">
                    <Trash2 size={13} />
                    Definitief verwijderen
                  </button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default TrashTab;
